import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { inviteMember, listEventPeople, removeMember } from '../../api/owner'
import type { EventPerson } from '../../api/ownerTypes'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Spinner } from '../../components/icons'

/**
 * Wie het feest mee beheert, en wie je er nog bij vraagt.
 *
 * Het woord "eigenaar" staat er nergens. Wie je hier uitnodigt is iemand die
 * meehelpt — je broer die de challenges verzint, de getuige die het album
 * exporteert — en niemand denkt bij zichzelf "ik ben mede-eigenaar van dit
 * feest". "Mensen" is wat het is.
 *
 * De lijst ziet iedereen die erbij mag; de knoppen alleen wie het feest
 * aanmaakte. Dat is `canManage` van de server, en niet iets dat dit scherm zelf
 * afleidt: de server weigert het toch, en twee plekken die hetzelfde moeten
 * weten lopen uiteen.
 */
export function EventPeople({ eventId }: { eventId: string }) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [removing, setRemoving] = useState<EventPerson | null>(null)

  const people = useQuery({
    queryKey: ['owner', 'people', eventId],
    queryFn: () => listEventPeople(eventId),
  })

  const invite = useMutation({
    mutationFn: (address: string) => inviteMember(eventId, address),
    onSuccess: async () => {
      setEmail('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['owner', 'people', eventId] })
    },
    onError: (cause) => {
      setError(cause instanceof Error ? cause.message : 'Uitnodigen lukte niet.')
    },
  })

  const drop = useMutation({
    mutationFn: (memberId: string) => removeMember(eventId, memberId),
    onSuccess: async () => {
      setRemoving(null)
      await queryClient.invalidateQueries({ queryKey: ['owner', 'people', eventId] })
    },
  })

  function submit(submission: FormEvent) {
    submission.preventDefault()

    if (email.trim()) {
      invite.mutate(email.trim())
    }
  }

  if (people.isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="text-accent size-5" />
      </div>
    )
  }

  const canManage = people.data?.canManage ?? false

  return (
    <>
      <div className="rounded-card border-line-card bg-surface overflow-hidden border">
        {people.data?.people.map((person, index) => (
          <div
            key={person.id}
            className={
              'flex items-center gap-3.5 p-4 lg:px-5 ' +
              (index > 0 ? 'border-line-menu border-t' : '')
            }
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[15px] font-semibold">
                {person.displayName ?? person.email}
              </span>
              <span className="text-ink-faint mt-0.5 block truncate text-[13px]">
                {person.displayName ? person.email : label(person)}
              </span>
            </span>

            <PersonStatus person={person} />

            {canManage && !person.isCreator && (
              <button
                type="button"
                aria-label={`${person.email} verwijderen`}
                onClick={() => setRemoving(person)}
                className="text-ink-ghost hover:text-accent shrink-0 cursor-pointer px-1 text-lg transition-colors"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>

      {canManage ? (
        <form onSubmit={submit} className="mt-3.5 flex flex-col gap-2.5 sm:flex-row">
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="naam@voorbeeld.be"
            autoComplete="off"
            className="rounded-notice border-line-card bg-surface focus:border-accent min-w-0 flex-1 border px-4 py-3 text-base transition-colors outline-none"
          />
          <button
            type="submit"
            disabled={!email.trim() || invite.isPending}
            className="bg-accent text-accent-ink h-12.5 shrink-0 cursor-pointer rounded-full px-6 text-[15px] font-semibold transition hover:brightness-95 disabled:opacity-55"
          >
            {invite.isPending ? 'Versturen…' : 'Uitnodigen'}
          </button>
        </form>
      ) : (
        <p className="text-ink-faint mt-3 text-[13px] leading-[1.5]">
          Alleen wie dit event aanmaakte kan mensen uitnodigen of verwijderen.
        </p>
      )}

      {error && (
        <p role="alert" className="text-accent mt-2.5 text-sm">
          {error}
        </p>
      )}

      {canManage && !error && (
        <p className="text-ink-faint mt-3 text-[13px] leading-[1.5] text-pretty">
          Wie je uitnodigt krijgt een mail en kan alles wat jij kan, behalve zelf mensen uitnodigen
          of verwijderen.
        </p>
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Deze persoon verwijderen?"
        detail={
          removing?.status === 'Accepted'
            ? `${removing.displayName ?? removing.email} kan dit event daarna niet meer beheren.`
            : `De uitnodiging aan ${removing?.email} vervalt. Je kan hem later opnieuw sturen.`
        }
        confirmLabel="Ja, verwijderen"
        cancelLabel="Laat staan"
        onConfirm={() => removing && drop.mutate(removing.id)}
        onCancel={() => setRemoving(null)}
      />
    </>
  )
}

function PersonStatus({ person }: { person: EventPerson }) {
  if (person.isCreator) {
    return (
      <span className="border-line-warm text-ink-faint shrink-0 rounded-full border px-2.5 py-1 text-[11px] tracking-[0.08em] uppercase">
        Maker
      </span>
    )
  }

  if (person.status === 'Pending') {
    return (
      <span className="bg-notice border-notice-line text-ink-soft shrink-0 rounded-full border px-2.5 py-1 text-[11px] tracking-[0.08em] uppercase">
        Wacht
      </span>
    )
  }

  if (person.status === 'Declined') {
    return (
      <span className="text-ink-faint shrink-0 rounded-full px-2.5 py-1 text-[11px] tracking-[0.08em] uppercase">
        Geweigerd
      </span>
    )
  }

  return (
    <span className="text-accent shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-[0.08em] uppercase">
      Beheert mee
    </span>
  )
}

/** De regel onder het adres zolang we nog geen naam kennen. */
function label(person: EventPerson): string {
  if (person.status === 'Pending') {
    return 'Uitgenodigd, nog geen antwoord'
  }

  return person.status === 'Declined' ? 'Heeft geweigerd' : 'Beheert mee'
}
